/**
 * Clientul HTTP. Un singur loc prin care trece fiecare octet dintre SPA si Hono.
 *
 * De ce un wrapper si nu `fetch` direct in hooks:
 *
 *   1. **`credentials: 'include'`**. Sesiunea e livrata printr-un COOKIE `HttpOnly`. Un
 *      `fetch` fara `credentials` il omite pe cross-origin si, mai important, uita sa-l trimita in
 *      exact scenariul in care ne doare (preview deployments pe alt subdomeniu). Uitarea lui o data
 *      inseamna 401-uri intermitente pe care nu le reproduci in dev, unde Vite face proxy pe
 *      acelasi origin.
 *   2. **`X-Request-Id`**. Serverul il preia in `requestContext` si il scrie in `audit_log`. Cand
 *      cineva intreaba „de ce s-a schimbat suma asta", raspunsul se cauta dupa acest id, nu dupa ora.
 *   3. **Timeout**. `fetch` nu are unul. Fara `AbortSignal`, o celula care salveaza peste un VPN
 *      cazut ramane in `salvez` pana la refresh — adica minte, spunand ca inca incearca.
 *   4. **Erorile tipate**. Plicul `{ error: { code, message, details } }` devine `ApiError` INAINTE
 *      de a ajunge la TanStack Query, ca politica de reincercare sa poata deosebi un 409 de un 503.
 *
 * Banii nu sunt atinsi nicaieri in fisierul asta: sumele traverseaza reteaua ca intregi in unitati
 * minore si ajung in cache tot asa. `JSON.parse` nu are voie sa devina o conversie in float.
 */

import { ApiError, eroareDinPlic, type ContextEroare } from './api-errors.ts';

/** Toate rutele sunt montate sub `/api` (Hono `basePath`), iar Vite face proxy pe el in dev. */
export const BAZA_API = '/api';

/**
 * 20 s. Mai mult decat cea mai lenta ruta reala (`/api/matrice` pe 300 de furnizori) si mult sub
 * pragul la care un om decide ca aplicatia s-a blocat.
 */
export const TIMEOUT_IMPLICIT_MS = 20_000;

/** Incarcarea unui fisier de 15 MB peste un VPN nu incape in 20 s. */
export const TIMEOUT_INCARCARE_MS = 120_000;

export type ValoareInterogare =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly (string | number)[];

export type Interogare = Readonly<Record<string, ValoareInterogare>>;

export type Metoda = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface OptiuniCerere {
  interogare?: Interogare;
  corp?: unknown;
  /** Semnalul apelantului (anularea unei interogari de TanStack Query). Se compune cu timeoutul. */
  semnal?: AbortSignal;
  timeoutMs?: number;
  antete?: Readonly<Record<string, string>>;
  /** Se poate fixa din afara ca sa se coreleze mai multe cereri ale aceleiasi actiuni. */
  requestId?: string;
}

/**
 * Serializarea parametrilor.
 *
 * Doua conventii impuse de API si respectate aici, nu reinventate in fiecare hook:
 *   - listele de id-uri merg ca `1,2,3` (schema `IdList` din `functions/api/schemas.ts`), pentru ca
 *     grila cere zeci de entitati intr-o singura cerere;
 *   - boolenele merg ca `1` / `0`, forma pe care o accepta schemele (`z.literal('1')`), nu `true`.
 */
export function construiesteInterogare(interogare?: Interogare): string {
  if (!interogare) return '';
  const parametri = new URLSearchParams();
  for (const [cheie, valoare] of Object.entries(interogare)) {
    if (valoare === undefined || valoare === null) continue;
    if (Array.isArray(valoare)) {
      if (valoare.length === 0) continue;
      parametri.set(cheie, valoare.join(','));
      continue;
    }
    if (typeof valoare === 'boolean') {
      parametri.set(cheie, valoare ? '1' : '0');
      continue;
    }
    parametri.set(cheie, String(valoare));
  }
  const sir = parametri.toString();
  return sir === '' ? '' : `?${sir}`;
}

export function construiesteUrl(cale: string, interogare?: Interogare): string {
  const normalizata = cale.startsWith('/') ? cale : `/${cale}`;
  return `${BAZA_API}${normalizata}${construiesteInterogare(interogare)}`;
}

/** `crypto.randomUUID` exista in toate tintele; rezerva acopera contextele non-secure din dev. */
export function idCerere(): string {
  const c: { randomUUID?: () => string } | undefined = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Compune semnalul apelantului cu cel de timeout.
 *
 * Nu se foloseste `AbortSignal.any`: e prea nou pentru a fi presupus in toate browserele tinta, iar
 * varianta manuala e cateva randuri si se poate testa fara un mediu de browser.
 */
function compuneSemnal(
  semnalExtern: AbortSignal | undefined,
  timeoutMs: number,
): { semnal: AbortSignal; curata: () => void; expirat: () => boolean } {
  const controler = new AbortController();
  let expirat = false;

  const cronometru = setTimeout(() => {
    expirat = true;
    controler.abort();
  }, timeoutMs);

  const laAnulare = (): void => controler.abort();
  if (semnalExtern) {
    if (semnalExtern.aborted) controler.abort();
    else semnalExtern.addEventListener('abort', laAnulare, { once: true });
  }

  return {
    semnal: controler.signal,
    curata: () => {
      clearTimeout(cronometru);
      semnalExtern?.removeEventListener('abort', laAnulare);
    },
    expirat: () => expirat,
  };
}

/**
 * Citeste corpul FARA sa arunce.
 *
 * Un 502 de la proxy vine ca HTML, iar un 204 nu vine deloc. In ambele cazuri raspunsul corect e
 * „nu am putut citi un plic", nu o exceptie de parsare care ar ascunde statusul real.
 */
async function citesteCorp(raspuns: Response): Promise<unknown> {
  if (raspuns.status === 204 || raspuns.status === 205) return null;
  const text = await raspuns.text().catch(() => '');
  if (text === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { textBrut: text.slice(0, 500) };
  }
}

/**
 * Cererea propriu-zisa.
 *
 * Contractul catre apelant: intoarce `T` sau ARUNCA un `ApiError`. Nu exista a treia varianta si nu
 * exista `{ ok, data }` — un rezultat care trebuie despachetat inainte de a fi verificat se
 * despacheteaza uneori fara verificare.
 */
export async function cerere<T>(
  metoda: Metoda,
  cale: string,
  optiuni: OptiuniCerere = {},
): Promise<T> {
  const url = construiesteUrl(cale, optiuni.interogare);
  const requestId = optiuni.requestId ?? idCerere();
  const context: ContextEroare = { metoda, cale, requestId };

  const antete: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
    ...optiuni.antete,
  };

  const areCorp = optiuni.corp !== undefined;
  if (areCorp) antete['Content-Type'] = 'application/json';

  const { semnal, curata, expirat } = compuneSemnal(
    optiuni.semnal,
    optiuni.timeoutMs ?? TIMEOUT_IMPLICIT_MS,
  );

  let raspuns: Response;
  try {
    raspuns = await fetch(url, {
      method: metoda,
      headers: antete,
      // Sesiunea Access e un cookie: fara asta, fiecare cerere e un 401 curat si inexplicabil.
      credentials: 'include',
      // Datele de trezorerie nu au voie sa vina din cache-ul browserului; vechimea se afiseaza cu ora.
      cache: 'no-store',
      redirect: 'follow',
      signal: semnal,
      ...(areCorp ? { body: JSON.stringify(optiuni.corp) } : {}),
    });
  } catch (cauza) {
    // Distinctia conteaza: la timeout stim ca cererea a PLECAT (poate a si scris), la o eroare de
    // retea stim ca nu a ajuns. Prima nu se reia automat pe o mutatie, a doua da.
    if (expirat()) {
      throw new ApiError({
        status: 0,
        code: 'timeout',
        message: `Serverul nu a răspuns în ${Math.round((optiuni.timeoutMs ?? TIMEOUT_IMPLICIT_MS) / 1000)} s.`,
        context,
      });
    }
    if (optiuni.semnal?.aborted) {
      throw new ApiError({ status: 0, code: 'anulat', message: 'Cerere anulată.', context });
    }
    throw new ApiError({
      status: 0,
      code: 'retea',
      message: 'Nu am putut contacta serverul.',
      details: cauza instanceof Error ? cauza.message : String(cauza),
      context,
    });
  } finally {
    curata();
  }

  const corp = await citesteCorp(raspuns);

  if (!raspuns.ok) {
    // `X-Request-Id` de pe raspuns bate pe cel trimis: daca a intervenit Cloudflare, `Cf-Ray`-ul
    // lui e cel care se regaseste in `audit_log`.
    const idServer = raspuns.headers?.get?.('X-Request-Id') ?? null;
    throw eroareDinPlic(raspuns.status, corp, {
      ...context,
      requestId: idServer ?? requestId,
    });
  }

  return corp as T;
}

export const api = {
  get: <T>(cale: string, optiuni?: Omit<OptiuniCerere, 'corp'>): Promise<T> =>
    cerere<T>('GET', cale, optiuni),
  post: <T>(cale: string, corp?: unknown, optiuni?: OptiuniCerere): Promise<T> =>
    cerere<T>('POST', cale, { ...optiuni, corp }),
  patch: <T>(cale: string, corp: unknown, optiuni?: OptiuniCerere): Promise<T> =>
    cerere<T>('PATCH', cale, { ...optiuni, corp }),
  put: <T>(cale: string, corp: unknown, optiuni?: OptiuniCerere): Promise<T> =>
    cerere<T>('PUT', cale, { ...optiuni, corp }),
  sterge: <T>(cale: string, optiuni?: OptiuniCerere): Promise<T> =>
    cerere<T>('DELETE', cale, optiuni),
} as const;

// ---------------------------------------------------------------------------
// Incarcare multipart, cu progres
// ---------------------------------------------------------------------------

export interface OptiuniIncarcare {
  /** 0..1. `fetch` NU raporteaza progresul de upload, deci calea cu progres trece prin XHR. */
  onProgres?: (fractie: number) => void;
  semnal?: AbortSignal;
  timeoutMs?: number;
  requestId?: string;
}

/**
 * `POST /api/attachments` — multipart.
 *
 * Se foloseste `XMLHttpRequest`, singurul API de browser care raporteaza progresul de UPLOAD
 * (`fetch` raporteaza doar download). Un extras de 12 MB fara bara de progres arata identic cu o
 * aplicatie blocata. Cand `XMLHttpRequest` lipseste (teste in Node), se cade elegant pe `fetch`,
 * fara progres — comportamentul de eroare rămâne identic, ceea ce conteaza pentru teste.
 */
export async function incarcaFisier<T>(
  cale: string,
  formular: FormData,
  optiuni: OptiuniIncarcare = {},
): Promise<T> {
  const url = construiesteUrl(cale);
  const requestId = optiuni.requestId ?? idCerere();
  const context: ContextEroare = { metoda: 'POST', cale, requestId };
  const timeoutMs = optiuni.timeoutMs ?? TIMEOUT_INCARCARE_MS;

  const XHR = (globalThis as { XMLHttpRequest?: typeof XMLHttpRequest }).XMLHttpRequest;
  if (typeof XHR !== 'function' || !optiuni.onProgres) {
    const { semnal, curata, expirat } = compuneSemnal(optiuni.semnal, timeoutMs);
    let raspuns: Response;
    try {
      raspuns = await fetch(url, {
        method: 'POST',
        // NU se pune `Content-Type`: browserul trebuie sa il genereze cu `boundary`-ul lui.
        headers: { Accept: 'application/json', 'X-Request-Id': requestId },
        credentials: 'include',
        body: formular,
        signal: semnal,
      });
    } catch (cauza) {
      if (expirat()) {
        throw new ApiError({ status: 0, code: 'timeout', message: 'Încărcarea a expirat.', context });
      }
      throw new ApiError({
        status: 0,
        code: 'retea',
        message: 'Nu am putut contacta serverul.',
        details: cauza instanceof Error ? cauza.message : String(cauza),
        context,
      });
    } finally {
      curata();
    }
    const corp = await citesteCorp(raspuns);
    if (!raspuns.ok) throw eroareDinPlic(raspuns.status, corp, context);
    return corp as T;
  }

  return new Promise<T>((rezolva, respinge) => {
    const xhr = new XHR();
    xhr.open('POST', url, true);
    xhr.responseType = 'text';
    xhr.withCredentials = true;
    xhr.timeout = timeoutMs;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Request-Id', requestId);

    xhr.upload.addEventListener('progress', (eveniment: ProgressEvent) => {
      if (eveniment.lengthComputable && eveniment.total > 0) {
        optiuni.onProgres?.(eveniment.loaded / eveniment.total);
      }
    });

    const laAnulare = (): void => xhr.abort();
    optiuni.semnal?.addEventListener('abort', laAnulare, { once: true });

    const inchide = (): void => optiuni.semnal?.removeEventListener('abort', laAnulare);

    xhr.addEventListener('load', () => {
      inchide();
      let corp: unknown = null;
      try {
        corp = xhr.responseText === '' ? null : (JSON.parse(xhr.responseText) as unknown);
      } catch {
        corp = { textBrut: xhr.responseText.slice(0, 500) };
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        optiuni.onProgres?.(1);
        rezolva(corp as T);
        return;
      }
      respinge(eroareDinPlic(xhr.status, corp, context));
    });

    xhr.addEventListener('error', () => {
      inchide();
      respinge(
        new ApiError({ status: 0, code: 'retea', message: 'Nu am putut contacta serverul.', context }),
      );
    });
    xhr.addEventListener('timeout', () => {
      inchide();
      respinge(new ApiError({ status: 0, code: 'timeout', message: 'Încărcarea a expirat.', context }));
    });
    xhr.addEventListener('abort', () => {
      inchide();
      respinge(new ApiError({ status: 0, code: 'anulat', message: 'Încărcare anulată.', context }));
    });

    xhr.send(formular);
  });
}

/**
 * Utilitar de tipuri: face optionale campurile pe care serverul le completeaza cu `.default(...)`.
 *
 * Tipul dedus dintr-o schema Zod e cel de IESIRE (dupa aplicarea implicitelor), deci `kind` sau
 * `dryRun` apar ca obligatorii desi clientul nu trebuie sa le trimita. Aici se pastreaza contractul
 * din `schemas.ts` ca sursa unica si se relaxeaza doar ce are implicit pe server.
 */
export type CuImplicite<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
